import React, { useEffect, useRef, useState } from "react";
import Navbar from "react-bootstrap/Navbar";
import Nav from "react-bootstrap/Nav";
import Container from "react-bootstrap/Container";
import Button from "react-bootstrap/Button";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  AiOutlineHome,
  AiOutlineFundProjectionScreen,
  AiOutlineMail,
  AiOutlineUser,
} from "react-icons/ai";
import { CgFileDocument } from "react-icons/cg";
import { normalizeProjectFocus } from "../data/projectData";

const welcomeStorageKey = "portfolio-candy-welcomed";
const welcomeScene = {
  mood: "hello",
  message: "Candy says Om builds clear products people trust fast.",
  moodDuration: 2200,
  messageDuration: 4600,
};
const avatarIdleScenes = [
  { mood: "peek", message: "", moodDuration: 1550 },
  { mood: "jump", message: "", moodDuration: 1120 },
  { mood: "hide-o", message: "", moodDuration: 2200 },
  { mood: "shy", message: "", moodDuration: 1500 },
  { mood: "wink-left", message: "", moodDuration: 1100 },
  { mood: "wink-right", message: "", moodDuration: 1100 },
];
const avatarClickScenes = [
  {
    mood: "jump",
    message: "Candy says Om ships polished work without the noise.",
    moodDuration: 1320,
    messageDuration: 3400,
  },
  {
    mood: "hide-o",
    message: "Candy says Om makes clever UI feel effortless.",
    moodDuration: 2200,
    messageDuration: 3400,
  },
  {
    mood: "peek",
    message: "Candy says Om turns busy flows into calm screens.",
    moodDuration: 1680,
    messageDuration: 3400,
  },
  {
    mood: "laugh",
    message: "Candy says Om has taste for details that actually matter.",
    moodDuration: 1500,
    messageDuration: 3500,
  },
  {
    mood: "shy",
    message: "Candy says Om builds with rare care.",
    moodDuration: 1460,
    messageDuration: 3200,
  },
];

function pickRandomAvatarScene(scenes, previousMood) {
  const availableScenes =
    scenes.length > 1
      ? scenes.filter((scene) => scene.mood !== previousMood)
      : scenes;

  return availableScenes[Math.floor(Math.random() * availableScenes.length)] || scenes[0];
}

function NavBar() {
  const [expand, updateExpanded] = useState(false);
  const [navColour, updateNavbar] = useState(false);
  const [avatarMood, setAvatarMood] = useState("idle");
  const [avatarMessage, setAvatarMessage] = useState("");
  const [isAvatarBlinking, setIsAvatarBlinking] = useState(false);
  const brandAvatarRef = useRef(null);
  const avatarTimersRef = useRef([]);
  const lastIdleMoodRef = useRef("idle");
  const avatarSceneApiRef = useRef({
    playScene: () => {},
  });
  const location = useLocation();
  const currentSearchParams = new URLSearchParams(location.search);
  const currentFocus = normalizeProjectFocus(
    currentSearchParams.get("focus")?.trim() || ""
  );
  const projectBriefTarget = {
    pathname: "/",
    search: currentFocus ? `?focus=${encodeURIComponent(currentFocus)}` : "",
    hash: "#project-brief",
  };

  const clearAvatarTimers = () => {
    if (typeof window === "undefined") {
      return;
    }

    avatarTimersRef.current.forEach((timerId) => {
      window.clearTimeout(timerId);
    });
    avatarTimersRef.current = [];
  };

  const scheduleAvatarTimer = (callback, delay) => {
    if (typeof window === "undefined") {
      return 0;
    }

    const timerId = window.setTimeout(callback, delay);
    avatarTimersRef.current.push(timerId);
    return timerId;
  };

  avatarSceneApiRef.current.playScene = (scene, { queueIdle = false } = {}) => {
    if (typeof window === "undefined") {
      return;
    }

    clearAvatarTimers();

    const moodDuration = scene.moodDuration ?? 1500;
    const messageDuration = scene.message
      ? scene.messageDuration ?? moodDuration + 1800
      : 0;

    setAvatarMood(scene.mood);
    setAvatarMessage(scene.message || "");

    scheduleAvatarTimer(() => {
      setAvatarMood("idle");
    }, moodDuration);

    if (scene.message) {
      scheduleAvatarTimer(() => {
        setAvatarMessage("");
      }, messageDuration);
    }

    if (queueIdle) {
      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      );

      if (!prefersReducedMotion.matches) {
        scheduleAvatarTimer(() => {
          const nextScene = pickRandomAvatarScene(
            avatarIdleScenes,
            lastIdleMoodRef.current
          );

          lastIdleMoodRef.current = nextScene.mood;
          avatarSceneApiRef.current.playScene(nextScene, { queueIdle: true });
        }, Math.max(moodDuration, messageDuration) + 2800 + Math.random() * 3200);
      }
    }
  };

  useEffect(() => {
    const scrollHandler = () => {
      updateNavbar(window.scrollY >= 20);
    };

    scrollHandler();
    window.addEventListener("scroll", scrollHandler);

    return () => window.removeEventListener("scroll", scrollHandler);
  }, []);

  useEffect(() => {
    return () => {
      clearAvatarTimers();
    };
  }, []);

  useEffect(() => {
    const avatarElement = brandAvatarRef.current;

    if (!avatarElement || typeof window === "undefined") {
      return undefined;
    }

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    );
    const usesCoarsePointer = window.matchMedia("(pointer: coarse)");

    if (prefersReducedMotion.matches || usesCoarsePointer.matches) {
      return undefined;
    }

    let animationFrame = 0;
    let currentX = 0;
    let currentY = 0;
    let targetX = 0;
    let targetY = 0;

    const renderLook = () => {
      currentX += (targetX - currentX) * 0.14;
      currentY += (targetY - currentY) * 0.14;

      avatarElement.style.setProperty("--brand-look-x", currentX.toFixed(3));
      avatarElement.style.setProperty("--brand-look-y", currentY.toFixed(3));

      if (
        Math.abs(targetX - currentX) > 0.002 ||
        Math.abs(targetY - currentY) > 0.002
      ) {
        animationFrame = window.requestAnimationFrame(renderLook);
        return;
      }

      animationFrame = 0;
    };

    const scheduleLook = () => {
      if (!animationFrame) {
        animationFrame = window.requestAnimationFrame(renderLook);
      }
    };

    const handlePointerMove = (event) => {
      const avatarBounds = avatarElement.getBoundingClientRect();
      const avatarCenterX = avatarBounds.left + avatarBounds.width / 2;
      const avatarCenterY = avatarBounds.top + avatarBounds.height * 0.42;
      const horizontalRange = Math.max(window.innerWidth * 0.16, 180);
      const verticalRange = Math.max(window.innerHeight * 0.22, 150);

      targetX = Math.max(
        -1,
        Math.min(1, (event.clientX - avatarCenterX) / horizontalRange)
      );
      targetY = Math.max(
        -1,
        Math.min(1, (event.clientY - avatarCenterY) / verticalRange)
      );
      scheduleLook();
    };

    const resetLook = () => {
      targetX = 0;
      targetY = 0;
      scheduleLook();
    };

    window.addEventListener("pointermove", handlePointerMove, {
      passive: true,
    });
    window.addEventListener("blur", resetLook);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("blur", resetLook);
      window.cancelAnimationFrame(animationFrame);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    let blinkDelayTimer = 0;
    let blinkResetTimer = 0;

    const scheduleBlink = () => {
      blinkDelayTimer = window.setTimeout(() => {
        setIsAvatarBlinking(true);
        blinkResetTimer = window.setTimeout(() => {
          setIsAvatarBlinking(false);
          scheduleBlink();
        }, 160);
      }, 3200 + Math.random() * 2600);
    };

    scheduleBlink();

    return () => {
      window.clearTimeout(blinkDelayTimer);
      window.clearTimeout(blinkResetTimer);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    );
    let hasWelcomed = false;

    try {
      hasWelcomed = window.sessionStorage.getItem(welcomeStorageKey) === "true";
    } catch {
      hasWelcomed = false;
    }

    if (hasWelcomed) {
      if (!prefersReducedMotion.matches) {
        scheduleAvatarTimer(() => {
          const nextScene = pickRandomAvatarScene(
            avatarIdleScenes,
            lastIdleMoodRef.current
          );

          lastIdleMoodRef.current = nextScene.mood;
          avatarSceneApiRef.current.playScene(nextScene, { queueIdle: true });
        }, 3600 + Math.random() * 2400);
      }

      return undefined;
    }

    lastIdleMoodRef.current = welcomeScene.mood;
    avatarSceneApiRef.current.playScene(welcomeScene, { queueIdle: true });

    try {
      window.sessionStorage.setItem(welcomeStorageKey, "true");
    } catch {
      // Ignore storage failures and keep the welcome visible.
    }

    return undefined;
  }, []);

  const handleAvatarClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const reaction = pickRandomAvatarScene(
      avatarClickScenes,
      avatarMood === "idle" ? lastIdleMoodRef.current : avatarMood
    );

    lastIdleMoodRef.current = reaction.mood;
    avatarSceneApiRef.current.playScene(reaction, { queueIdle: true });
  };

  const avatarClassName = [
    "brand-avatar",
    isAvatarBlinking ? "is-blinking" : "",
    avatarMood !== "idle" ? `is-${avatarMood}` : "",
  ]
    .filter(Boolean)
    .join(" ");

  const brandShellClassName = [
    "navbar-brand navbar-brand-shell",
    avatarMood === "hide-o" ? "is-hide-o" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Navbar
      expanded={expand}
      fixed="top"
      expand="lg"
      className={navColour ? "sticky" : "navbar"}
      aria-label="Primary"
    >
      <Container className="section-shell">
        <div className={brandShellClassName}>
          <button
            type="button"
            className={avatarClassName}
            onClick={handleAvatarClick}
            ref={brandAvatarRef}
            aria-label="Play with Candy, the welcome mascot"
          >
            <span className="brand-avatar-halo"></span>
            <span className="brand-avatar-shoulders"></span>
            <span className="brand-avatar-head">
              <span className="brand-avatar-bow"></span>
              <span className="brand-avatar-hair"></span>
              <span className="brand-avatar-cheek brand-avatar-cheek-left"></span>
              <span className="brand-avatar-cheek brand-avatar-cheek-right"></span>
              <span className="brand-avatar-eye brand-avatar-eye-left">
                <span className="brand-avatar-pupil"></span>
              </span>
              <span className="brand-avatar-eye brand-avatar-eye-right">
                <span className="brand-avatar-pupil"></span>
              </span>
              <span className="brand-avatar-smile"></span>
            </span>
          </button>

          <Link
            to="/"
            onClick={() => updateExpanded(false)}
            className="navbar-brand-link"
            aria-label="Go to Om Jaiswal homepage"
          >
            <span className="brand-name" aria-hidden="true">
              <span className="brand-name-o">
                {avatarMood === "hide-o" ? (
                  <span className="brand-name-o-candy">
                    <span className="brand-name-o-candy-head">
                      <span className="brand-name-o-candy-bow"></span>
                      <span className="brand-name-o-candy-hair"></span>
                      <span className="brand-name-o-candy-eye brand-name-o-candy-eye-left"></span>
                      <span className="brand-name-o-candy-eye brand-name-o-candy-eye-right"></span>
                      <span className="brand-name-o-candy-smile"></span>
                    </span>
                  </span>
                ) : null}
                <span className="brand-name-o-letter">O</span>
              </span>
              <span className="brand-name-rest">m Jaiswal</span>
            </span>
          </Link>

          {avatarMessage ? (
            <span className="brand-avatar-bubble" role="status" aria-live="polite">
              {avatarMessage}
            </span>
          ) : null}
        </div>
        <Navbar.Toggle
          aria-controls="responsive-navbar-nav"
          aria-label="Toggle navigation"
          onClick={() => {
            updateExpanded(expand ? false : "expanded");
          }}
        >
          <span></span>
          <span></span>
          <span></span>
        </Navbar.Toggle>
        <Navbar.Collapse id="responsive-navbar-nav">
          <Nav className="ms-auto" defaultActiveKey="#home">
            <Nav.Item>
              <Nav.Link
                as={NavLink}
                end
                to="/"
                onClick={() => updateExpanded(false)}
              >
                <AiOutlineHome />
                <span>Home</span>
              </Nav.Link>
            </Nav.Item>

            <Nav.Item>
              <Nav.Link
                as={NavLink}
                to="/about"
                onClick={() => updateExpanded(false)}
              >
                <AiOutlineUser />
                <span>About</span>
              </Nav.Link>
            </Nav.Item>

            <Nav.Item>
              <Nav.Link
                as={NavLink}
                to="/projects"
                onClick={() => updateExpanded(false)}
              >
                <AiOutlineFundProjectionScreen />
                <span>Projects</span>
              </Nav.Link>
            </Nav.Item>

            <Nav.Item>
              <Nav.Link
                as={NavLink}
                to="/experience"
                onClick={() => updateExpanded(false)}
              >
                <CgFileDocument />
                <span>Experience</span>
              </Nav.Link>
            </Nav.Item>

            <Nav.Item className="nav-cta-item">
              <Button
                as={Link}
                to={projectBriefTarget}
                className="nav-cta-button"
                aria-label="Go to project brief"
                onClick={() => updateExpanded(false)}
              >
                <AiOutlineMail />
                <span>Start a Project</span>
              </Button>
            </Nav.Item>
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
}

export default NavBar;
